import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Center, Group, Loader, Pagination, Select, Stack, Text, Title } from '@mantine/core';
import api from '../services/api';

interface Job {
  id: number;
  title: string;
  company_name: string;
  location: string;
  job_type: string;
  is_approved: boolean;
  is_active: boolean;
  applications_count?: number;
}

const MyPostedJobsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [reactivatingJobId, setReactivatingJobId] = useState<number | null>(null);
  const [deactivatingJobId, setDeactivatingJobId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    loadMyJobs();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const loadMyJobs = async () => {
    try {
      setLoading(true);
      const response = await api.getMyJobs();
      const jobsData = response;
      setJobs(jobsData);
    } catch (error) {
      console.error('Failed to load posted jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (jobId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setReactivatingJobId(jobId);
      await api.reactivateJob(jobId);
      setJobs((prev) =>
        prev.map((job) => (job.id === jobId ? { ...job, is_active: true } : job))
      );
    } catch (error) {
      console.error('Failed to reactivate job:', error);
    } finally {
      setReactivatingJobId(null);
    }
  };

  const handleDeactivate = async (jobId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setDeactivatingJobId(jobId);
      await api.deleteJob(jobId);
      setJobs((prev) =>
        prev.map((job) => (job.id === jobId ? { ...job, is_active: false } : job))
      );
    } catch (error) {
      console.error('Failed to deactivate job:', error);
    } finally {
      setDeactivatingJobId(null);
    }
  };

  if (!user || (user.user_type === 'JOB_SEEKER')) {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Title order={3}>{t('postedJobs.forPosters')}</Title>
        <Text c="dimmed">{t('postedJobs.onlyPosters')}</Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Card withBorder padding="xl" radius="lg" shadow="sm">
      <Stack gap="sm">
        <Title order={2}>{t('postedJobs.title')}</Title>
        <Text c="dimmed">{t('postedJobs.subtitle')}</Text>
        {jobs.length === 0 && <Text c="dimmed">{t('postedJobs.empty')}</Text>}
        <Group justify="space-between" align="center" wrap="wrap">
          <Select
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || 'ACTIVE')}
            data={[
              { value: 'ACTIVE', label: t('postedJobs.filterActive') },
              { value: 'INACTIVE', label: t('postedJobs.filterInactive') },
              { value: 'ALL', label: t('postedJobs.filterAll') },
            ]}
            style={{ width: '100%', maxWidth: 240 }}
          />
        </Group>
        <Stack gap="sm" mt="sm">
          {jobs
            .filter((job) => {
              if (statusFilter === 'ALL') return true;
              if (statusFilter === 'ACTIVE') return job.is_active;
              return !job.is_active;
            })
            .slice((page - 1) * pageSize, page * pageSize)
            .map((job) => (
              <Card
                key={job.id}
                component={Link}
                to={`/jobs/${job.id}`}
                withBorder
                radius="md"
                shadow="xs"
                padding="md"
                style={{ cursor: 'pointer', textDecoration: 'none' }}
              >
                <Group justify="space-between" align="center" wrap="wrap">
                  <div>
                    <Title order={5}>{job.title}</Title>
                    <Text c="dimmed" size="sm">{job.company_name} • {job.location}</Text>
                    <Text c="dimmed" size="xs">
                      {(job.applications_count ?? 0)} {t('postedJobs.applicants')}
                    </Text>
                    <Group gap="xs" mt="xs" wrap="wrap">
                      <Badge variant="light" color="orange">
                        {job.job_type.replace('_', ' ')}
                      </Badge>
                      <Badge variant="light" color={job.is_active ? 'green' : 'gray'}>
                        {job.is_active ? t('postedJobs.active') : t('postedJobs.inactive')}
                      </Badge>
                    </Group>
                  </div>
                  <Group gap="xs" wrap="wrap" align="flex-start">
                    {!job.is_approved && (
                      <Badge variant="light" color="red">{t('approvals.pendingApproval')}</Badge>
                    )}
                    {!job.is_active && (
                      <Badge variant="light" color="gray">{t('postedJobs.inactive')}</Badge>
                    )}
                    <Button
                      size="xs"
                      variant="light"
                      component={Link}
                      to={`/jobs/${job.id}/applications`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {t('postedJobs.viewPostulations')}
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      component={Link}
                      to={`/jobs/${job.id}/edit`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {t('common.editJob')}
                    </Button>
                    {job.is_active && (
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        onClick={(e) => handleDeactivate(job.id, e)}
                        loading={deactivatingJobId === job.id}
                      >
                        {t('postedJobs.deactivate')}
                      </Button>
                    )}
                    {!job.is_active && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={(e) => handleReactivate(job.id, e)}
                        loading={reactivatingJobId === job.id}
                      >
                        {t('postedJobs.reactivate')}
                      </Button>
                    )}
                  </Group>
                </Group>
              </Card>
            ))}
        </Stack>
        {jobs.filter((job) => {
          if (statusFilter === 'ALL') return true;
          if (statusFilter === 'ACTIVE') return job.is_active;
          return !job.is_active;
        }).length > pageSize && (
            <Group justify="center" mt="sm">
              <Pagination
                value={page}
                onChange={setPage}
                total={Math.max(
                  1,
                  Math.ceil(
                    jobs.filter((job) => {
                      if (statusFilter === 'ALL') return true;
                      if (statusFilter === 'ACTIVE') return job.is_active;
                      return !job.is_active;
                    }).length / pageSize
                  )
                )}
              />
            </Group>
          )}
      </Stack>
    </Card>
  );
};

export default MyPostedJobsPage;
